import { Dispatch } from 'redux';
import {
  requestFieldNamesByOperations,
  searchFieldsByName as searchFieldsByNameRequester,
} from '../requesters/uapiFieldsRequester/uapiFieldsRequester';
import type { FieldSearchIndex } from '../requesters/uapiFieldsRequester/uapiFieldsRequester';
import {
  receiveFieldSearchIndex,
  fieldSearchIndexFailure,
  receiveFieldNameSearchResults,
  clearFieldNameSearch,
} from '../actions/fieldsActions';
import type { FieldsActions } from '../actions/fieldsActions';
import { logDmbErrorLocation } from '../utils/error';
import type { OperationCurrent } from '../utils/operationUtils';
import { resolveCurrentOperationId, resolveOperationIds } from '../utils/operationUtils';

const INDEX_STORE_LIMIT = 500;
const SEARCH_DEBOUNCE_MS = 300;

function trimIndex(index: FieldSearchIndex): FieldSearchIndex {
  const keys = Object.keys(index);
  if (keys.length <= INDEX_STORE_LIMIT) return index;

  const trimmed: FieldSearchIndex = {};
  for (let i = 0; i < INDEX_STORE_LIMIT; i++) {
    trimmed[keys[i]] = index[keys[i]];
  }
  return trimmed;
}

interface UapiFieldsState {
  operations: {
    current: OperationCurrent;
    allIds: string[];
  };
}

export const fetchFieldSearchIndex = (operationIds: string[]) =>
  async (dispatch: Dispatch<FieldsActions>, getState: () => UapiFieldsState): Promise<void> => {
    try {
      const { operations } = getState();
      const currentOpId = resolveCurrentOperationId(operations.current, operationIds);
      const priorityIds = currentOpId ? [currentOpId] : [];
      const remainingIds = operationIds.filter((id) => id !== currentOpId);

      if (priorityIds.length > 0) {
        const priorityIndex = await requestFieldNamesByOperations(priorityIds);
        dispatch(receiveFieldSearchIndex(trimIndex(priorityIndex)));
      }

      if (remainingIds.length > 0) {
        const remainingIndex = await requestFieldNamesByOperations(remainingIds);
        dispatch(receiveFieldSearchIndex(trimIndex(remainingIndex)));
      }
    } catch (error) {
      dispatch(fieldSearchIndexFailure());
      logDmbErrorLocation(error, 'uapiFieldsThunks - fetchFieldSearchIndex');
    }
  };

let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

export const fetchFieldNameSearch = (searchTerm: string) =>
  (dispatch: Dispatch<FieldsActions>, getState: () => UapiFieldsState): void => {
    if (!searchTerm || searchTerm.trim().length === 0) {
      dispatch(clearFieldNameSearch());
      return;
    }

    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }

    searchDebounceTimer = setTimeout(async () => {
      try {
        const { operations } = getState();
        const filteredAllIds = operations.allIds.filter(id => id !== 'all-operations');
        const operationIds = resolveOperationIds(operations.current) ?? filteredAllIds;
        const results = await searchFieldsByNameRequester(operationIds, searchTerm.trim());
        dispatch(receiveFieldNameSearchResults(results, searchTerm));
      } catch (error) {
        logDmbErrorLocation(error, 'uapiFieldsThunks - fetchFieldNameSearch');
      }
    }, SEARCH_DEBOUNCE_MS);
  };
